import { useCallback, useEffect, useRef, useState } from 'react'

import {
  PROXY_API_V1_URL,
  createOntologyHeaders,
  getApiKey,
  getAuthenticationOrganizationId,
  getOntologyBranchHeaderValue,
  getOntologySiteHeaderValue
} from '../core'
import { trackDataFlight } from '../../telemetry/navigation-timing'
import type { GraphTagVendor } from './tag-vendor-types'

export const TAG_INVENTORY_PAGE_SIZE = 50

export type TagInventoryVendor = GraphTagVendor

export type TagInventoryParentKind = 'providers' | 'provider' | 'folder' | 'udt'
export type TagInventoryFolderKind = 'provider' | 'folder' | 'udt'

export interface TagInventoryTreeArgs {
  provider?: string
  folder?: string
  udt?: string
  pageToken?: string
  pageSize?: number
  /** Included in the hook cache key; the request itself is header-scoped. */
  branch?: string
  /** Included in the hook cache key; the request itself is header-scoped. */
  site?: string
}

export type TagInventoryAcquisitionFilter = 'all' | 'enabled' | 'disabled'
export type TagInventoryHistorySourceFilter = 'all' | 'webdev' | 'opc'
export type TagInventoryViewFilter =
  | 'all'
  | 'needs_registration'
  | 'problems'
  | 'warnings'
  | 'archived'

export interface TagInventoryFilteredArgs {
  provider?: string
  folder?: string
  udt?: string
  acquisition: TagInventoryAcquisitionFilter
  historySource: TagInventoryHistorySourceFilter
  view: TagInventoryViewFilter
  revealNodeId?: string
  pageToken?: string
  pageSize?: number
  branch?: string
  site?: string
}

export interface TagInventorySearchArgs {
  query: string
  pageToken?: string
  pageSize?: number
  branch?: string
  site?: string
}

export type TagInventoryMismatchStatus = 'all' | 'missing_registration' | 'missing_graph'

export interface TagInventoryMismatchesArgs {
  pageToken?: string
  pageSize?: number
  status?: TagInventoryMismatchStatus
  /** Included in the hook cache key; the request itself is header-scoped. */
  branch?: string
  /** Included in the hook cache key; the request itself is header-scoped. */
  site?: string
}

export interface TagInventoryRegistration {
  state: 'registered' | 'registry_only' | string
  tagId: string
  enabled: boolean
  scrapeMode: string
  protocol: string
  vendor: string
  frequencyMs: number
  sourceUrl: string
  quarantined: boolean
  opcNodeId: string | null
  /** A PI Web API WebId sidecar serves this tag's history (standalone or bound). */
  piHistory: boolean
  /** A Canary Views tag-name sidecar serves this tag's history (standalone or bound). */
  canaryHistory: boolean
}

export interface TagInventoryTag {
  name: string
  nodeId: string
  tagPath: string
  provider: string
  vendor: TagInventoryVendor
  opcEndpoint: string | null
  opcServer: string | null
  opcNodeId: string | null
  opcItemPath: string | null
  archived: boolean
  gatewayHistoryEnabled: boolean
  registration: TagInventoryRegistration | null
}

export interface TagInventoryFolder {
  id: string
  name: string
  path: string
  providerName: string
  depth: number
  kind: TagInventoryFolderKind
  folderless: boolean
  tagCount: number
  subtreeTagCount: number
  archivedTagCount: number
}

export interface TagInventoryParent {
  kind: TagInventoryParentKind
  id: string
  name: string
  folderless: boolean
  tagCount: number
  subtreeTagCount: number
  archivedTagCount: number
}

export interface TagInventoryTreeResponse {
  parent: TagInventoryParent
  folders: TagInventoryFolder[]
  tags: TagInventoryTag[]
  nextPageToken: string
  totalSize: number | null
}

export interface TagInventoryFilteredFolderCount {
  id: string
  matchingTagCount: number | null
  reason: string | null
  tagCountAtLeast: number | null
}

export interface TagInventoryFilteredFacetCounts {
  acquisition: {
    all: number | null
    enabled: number | null
    disabled: number | null
  }
  view: {
    all: number | null
    needsRegistration: number | null
    problems: number | null
    warnings: number | null
    archived: number | null
  }
}

export interface TagInventoryFilteredResponse {
  parent: TagInventoryParent
  folders: TagInventoryFolder[]
  tags: TagInventoryTag[]
  folderCounts: TagInventoryFilteredFolderCount[]
  filterStatus: 'computed' | 'not_computed'
  filterReason: string | null
  filterTagCountAtLeast: number | null
  matchingTagCount: number | null
  matchingCountReason: string | null
  matchingTagCountAtLeast: number | null
  facets: TagInventoryFilteredFacetCounts | null
  nextPageToken: string
  totalSize: number
}

export interface TagInventoryAncestor {
  id: string
  name: string
  kind: TagInventoryFolderKind
}

export interface TagInventorySearchHit {
  tag: TagInventoryTag
  score: number
  ancestors: TagInventoryAncestor[]
  revealPageToken: string
}

export interface TagInventorySearchResponse {
  results: TagInventorySearchHit[]
  nextPageToken: string
}

export interface TagInventoryMismatch extends TagInventoryTag {
  mismatchStatus: Exclude<TagInventoryMismatchStatus, 'all'>
}

export interface TagInventoryMismatchesResponse {
  tags: TagInventoryMismatch[]
  nextPageToken: string
  /** Mismatches matching the requested status. */
  totalSize: number | null
  /** Both directions are reported on every page, whatever status was requested. */
  missingRegistrationCount: number | null
  missingGraphCount: number | null
}

export type TagInventoryHealthStatus = 'ok' | 'warn' | 'bad' | 'unknown' | 'not_computed'

export type TagInventoryHealthReason =
  | 'registry_lookup_failed'
  | 'health_unavailable'
  | 'evaluation_failed'
  | 'no_recent_readings'
  | string

export interface TagInventoryHealthResult {
  id: string
  status: TagInventoryHealthStatus
  reason: TagInventoryHealthReason | null
}

export interface TagInventoryHealthResponse {
  windowSeconds: number
  results: TagInventoryHealthResult[]
}

export interface TagInventoryQueryResult<T> {
  data: T | undefined
  isLoading: boolean
  isPreviousData: boolean
  error: string | undefined
  refetch: () => void
}

const INVENTORY_QUERY_CACHE_TTL_MS = 30_000
const INVENTORY_QUERY_CACHE_MAX_ENTRIES = 100

interface InventoryQueryCacheEntry {
  data: unknown
  expiresAt: number
}

interface InventoryQueryFlight {
  controller: AbortController
  promise: Promise<unknown>
}

const inventoryQueryCache = new Map<string, InventoryQueryCacheEntry>()
const inventoryQueryFlights = new Map<string, InventoryQueryFlight>()

export function invalidateTagInventoryQueryCache(): void {
  for (const flight of inventoryQueryFlights.values()) flight.controller.abort()
  inventoryQueryFlights.clear()
  inventoryQueryCache.clear()
}

export const resetTagInventoryQueryCache = invalidateTagInventoryQueryCache

function inventoryQueryScopeKey(): string {
  return JSON.stringify({
    organization: getAuthenticationOrganizationId() ?? '',
    apiKey: getApiKey(),
    branch: getOntologyBranchHeaderValue() ?? '',
    site: getOntologySiteHeaderValue() ?? ''
  })
}

function writeInventoryQueryCache<T>(key: string, data: T): void {
  const now = Date.now()
  for (const [cachedKey, entry] of inventoryQueryCache) {
    if (entry.expiresAt <= now) inventoryQueryCache.delete(cachedKey)
  }
  while (inventoryQueryCache.size >= INVENTORY_QUERY_CACHE_MAX_ENTRIES) {
    const oldestKey = inventoryQueryCache.keys().next().value
    if (typeof oldestKey !== 'string') break
    inventoryQueryCache.delete(oldestKey)
  }
  inventoryQueryCache.set(key, {
    data,
    expiresAt: now + INVENTORY_QUERY_CACHE_TTL_MS
  })
}

function cancelInventoryQueryFlight(key: string): void {
  const flight = inventoryQueryFlights.get(key)
  if (!flight) return
  inventoryQueryFlights.delete(key)
  flight.controller.abort()
}

function inventoryQueryFlight<T>(
  key: string,
  fetcher: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  const existing = inventoryQueryFlights.get(key)
  if (existing) return existing.promise as Promise<T>

  const controller = new AbortController()
  const flight: InventoryQueryFlight = {
    controller,
    promise: Promise.resolve(undefined)
  }
  flight.promise = trackDataFlight(fetcher(controller.signal)).then(
    (result) => {
      if (inventoryQueryFlights.get(key) === flight) {
        inventoryQueryFlights.delete(key)
        writeInventoryQueryCache(key, result)
      }
      return result
    },
    (error: unknown) => {
      if (inventoryQueryFlights.get(key) === flight) {
        inventoryQueryFlights.delete(key)
      }
      throw error
    }
  )
  inventoryQueryFlights.set(key, flight)
  return flight.promise as Promise<T>
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function nullableStr(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function nullableNum(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function inventoryVendor(value: unknown): TagInventoryVendor {
  switch (value) {
    case 'ignition':
    case 'aveva':
    case 'factorysim':
    case 'opc':
    case 'db':
    case 'mtconnect':
    case 'pi':
    case 'canary':
      return value
    default:
      return 'unknown'
  }
}

function registrationFromJson(value: unknown): TagInventoryRegistration | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  return {
    state: str(record.state),
    tagId: str(record.tag_id),
    enabled: record.enabled === true,
    scrapeMode: str(record.scrape_mode),
    protocol: str(record.protocol),
    vendor: str(record.vendor),
    frequencyMs: num(record.frequency_ms),
    sourceUrl: str(record.source_url),
    quarantined: record.quarantined === true,
    opcNodeId: nullableStr(record.opc_node_id),
    piHistory: record.pi_history === true,
    canaryHistory: record.canary_history === true
  }
}

export function tagInventoryTagFromJson(value: unknown): TagInventoryTag {
  const record = (value ?? {}) as Record<string, unknown>
  return {
    name: str(record.name),
    nodeId: str(record.node_id),
    tagPath: str(record.tag_path),
    provider: str(record.provider),
    vendor: inventoryVendor(record.vendor),
    opcEndpoint: nullableStr(record.opc_endpoint),
    opcServer: nullableStr(record.opc_server),
    opcNodeId: nullableStr(record.opc_node_id),
    opcItemPath: nullableStr(record.opc_item_path),
    archived: record.archived === true,
    gatewayHistoryEnabled: record.gateway_history_enabled === true,
    registration: registrationFromJson(record.registration)
  }
}

function inventoryFolderFromJson(value: unknown): TagInventoryFolder {
  const record = (value ?? {}) as Record<string, unknown>
  return {
    id: str(record.id),
    name: str(record.name),
    path: str(record.path),
    providerName: str(record.provider_name),
    depth: num(record.depth),
    kind: record.kind === 'provider' || record.kind === 'udt' ? record.kind : 'folder',
    folderless: record.folderless === true,
    tagCount: num(record.tag_count),
    subtreeTagCount: num(record.subtree_tag_count),
    archivedTagCount: num(record.archived_tag_count)
  }
}

function inventoryParentFromJson(value: unknown): TagInventoryParent {
  const record = (value ?? {}) as Record<string, unknown>
  const kind =
    record.kind === 'provider' || record.kind === 'folder' || record.kind === 'udt'
      ? record.kind
      : ('providers' as const)
  return {
    kind,
    id: str(record.id),
    name: str(record.name),
    folderless: record.folderless === true,
    tagCount: num(record.tag_count),
    subtreeTagCount: num(record.subtree_tag_count),
    archivedTagCount: num(record.archived_tag_count)
  }
}

export function tagInventoryTreeFromJson(value: unknown): TagInventoryTreeResponse {
  const record = (value ?? {}) as Record<string, unknown>
  return {
    parent: inventoryParentFromJson(record.parent),
    folders: Array.isArray(record.folders) ? record.folders.map(inventoryFolderFromJson) : [],
    tags: Array.isArray(record.tags) ? record.tags.map(tagInventoryTagFromJson) : [],
    nextPageToken: str(record.next_page_token),
    totalSize: nullableNum(record.total_size)
  }
}

function nullableRecordNum(record: Record<string, unknown>, key: string): number | null {
  return record[key] === null || record[key] === undefined ? null : num(record[key])
}

export function tagInventoryFilteredFromJson(value: unknown): TagInventoryFilteredResponse {
  const record = (value ?? {}) as Record<string, unknown>
  const folderCounts = Array.isArray(record.folder_counts) ? record.folder_counts : []
  const facetsRecord =
    record.facets && typeof record.facets === 'object'
      ? (record.facets as Record<string, unknown>)
      : null
  const acquisition =
    facetsRecord?.acquisition && typeof facetsRecord.acquisition === 'object'
      ? (facetsRecord.acquisition as Record<string, unknown>)
      : null
  const view =
    facetsRecord?.view && typeof facetsRecord.view === 'object'
      ? (facetsRecord.view as Record<string, unknown>)
      : null
  return {
    parent: inventoryParentFromJson(record.parent),
    folders: Array.isArray(record.folders) ? record.folders.map(inventoryFolderFromJson) : [],
    tags: Array.isArray(record.tags) ? record.tags.map(tagInventoryTagFromJson) : [],
    folderCounts: folderCounts.map((value) => {
      const count = (value ?? {}) as Record<string, unknown>
      return {
        id: str(count.id),
        matchingTagCount: nullableRecordNum(count, 'matching_tag_count'),
        reason: nullableStr(count.reason),
        tagCountAtLeast: nullableNum(count.tag_count_at_least)
      }
    }),
    filterStatus: record.filter_status === 'not_computed' ? 'not_computed' : 'computed',
    filterReason: nullableStr(record.filter_reason),
    filterTagCountAtLeast: nullableNum(record.filter_tag_count_at_least),
    matchingTagCount: nullableNum(record.matching_tag_count),
    matchingCountReason: nullableStr(record.matching_count_reason),
    matchingTagCountAtLeast: nullableNum(record.matching_tag_count_at_least),
    facets:
      acquisition && view
        ? {
            acquisition: {
              all: nullableRecordNum(acquisition, 'all'),
              enabled: nullableRecordNum(acquisition, 'enabled'),
              disabled: nullableRecordNum(acquisition, 'disabled')
            },
            view: {
              all: nullableRecordNum(view, 'all'),
              needsRegistration: nullableRecordNum(view, 'needs_registration'),
              problems: nullableRecordNum(view, 'problems'),
              warnings: nullableRecordNum(view, 'warnings'),
              archived: nullableRecordNum(view, 'archived')
            }
          }
        : null,
    nextPageToken: str(record.next_page_token),
    totalSize: num(record.total_size)
  }
}

function inventoryAncestorFromJson(value: unknown): TagInventoryAncestor | null {
  const record = (value ?? {}) as Record<string, unknown>
  const kind =
    record.kind === 'provider' || record.kind === 'udt' || record.kind === 'folder'
      ? record.kind
      : null
  const id = str(record.id)
  if (!kind || !id) return null
  return { id, name: str(record.name), kind }
}

export function tagInventorySearchFromJson(value: unknown): TagInventorySearchResponse {
  const record = (value ?? {}) as Record<string, unknown>
  const results = Array.isArray(record.results) ? record.results : []
  return {
    results: results.map((value): TagInventorySearchHit => {
      const hit = (value ?? {}) as Record<string, unknown>
      const ancestors = Array.isArray(hit.ancestors)
        ? hit.ancestors
            .map(inventoryAncestorFromJson)
            .filter((ancestor): ancestor is TagInventoryAncestor => ancestor !== null)
        : []
      return {
        tag: tagInventoryTagFromJson(hit.tag),
        score: num(hit.score),
        ancestors,
        revealPageToken: str(hit.reveal_page_token)
      }
    }),
    nextPageToken: str(record.next_page_token)
  }
}

function mismatchStatus(value: unknown): TagInventoryMismatch['mismatchStatus'] {
  return value === 'missing_registration' ? 'missing_registration' : 'missing_graph'
}

export function tagInventoryMismatchesFromJson(value: unknown): TagInventoryMismatchesResponse {
  const record = (value ?? {}) as Record<string, unknown>
  return {
    tags: Array.isArray(record.tags)
      ? record.tags.map((value) => {
          const tag = tagInventoryTagFromJson(value)
          const row = (value ?? {}) as Record<string, unknown>
          return {
            ...tag,
            mismatchStatus: mismatchStatus(row.mismatch_status)
          }
        })
      : [],
    nextPageToken: str(record.next_page_token),
    totalSize: nullableNum(record.total_size),
    missingRegistrationCount: nullableNum(record.missing_registration_count),
    missingGraphCount: nullableNum(record.missing_graph_count)
  }
}

function healthStatus(value: unknown): TagInventoryHealthStatus {
  switch (value) {
    case 'ok':
    case 'warn':
    case 'bad':
    case 'unknown':
    case 'not_computed':
      return value
    default:
      return 'not_computed'
  }
}

export function tagInventoryHealthFromJson(value: unknown): TagInventoryHealthResponse {
  const record = (value ?? {}) as Record<string, unknown>
  const results = Array.isArray(record.results) ? record.results : []
  return {
    windowSeconds: num(record.window_seconds),
    results: results.map((value): TagInventoryHealthResult => {
      const result = (value ?? {}) as Record<string, unknown>
      return {
        id: str(result.id),
        status: healthStatus(result.status),
        reason: nullableStr(result.reason)
      }
    })
  }
}

async function requestInventoryJson(
  path: string,
  signal?: AbortSignal,
  body?: unknown
): Promise<unknown> {
  const headers = new Headers(createOntologyHeaders())
  if (body !== undefined) headers.set('Content-Type', 'application/json')
  const response = await fetch(`${PROXY_API_V1_URL}${path}`, {
    headers,
    method: body === undefined ? 'GET' : 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  })
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`
    try {
      const body = (await response.json()) as { message?: unknown }
      if (typeof body.message === 'string' && body.message.length > 0) detail = body.message
    } catch {
      // Keep the status-line fallback when the body is not JSON.
    }
    throw new Error(detail)
  }
  return response.json()
}

export async function fetchTagInventoryTree(
  args: TagInventoryTreeArgs = {},
  signal?: AbortSignal
): Promise<TagInventoryTreeResponse> {
  const params = new URLSearchParams()
  if (args.provider) params.set('provider', args.provider)
  if (args.folder) params.set('folder', args.folder)
  if (args.udt) params.set('udt', args.udt)
  if (args.pageToken) params.set('page_token', args.pageToken)
  params.set('page_size', String(args.pageSize ?? TAG_INVENTORY_PAGE_SIZE))
  const response = await requestInventoryJson(
    `/ontology/tags/inventory:tree?${params.toString()}`,
    signal
  )
  return tagInventoryTreeFromJson(response)
}

export async function fetchTagInventoryFiltered(
  args: TagInventoryFilteredArgs,
  signal?: AbortSignal
): Promise<TagInventoryFilteredResponse> {
  const params = new URLSearchParams({
    acquisition: args.acquisition,
    history_source: args.historySource,
    view: args.view
  })
  if (args.provider) params.set('provider', args.provider)
  if (args.folder) params.set('folder', args.folder)
  if (args.udt) params.set('udt', args.udt)
  if (args.revealNodeId) params.set('reveal_node_id', args.revealNodeId)
  if (args.pageToken) params.set('page_token', args.pageToken)
  params.set('page_size', String(args.pageSize ?? TAG_INVENTORY_PAGE_SIZE))
  const response = await requestInventoryJson(
    `/ontology/tags/inventory:filtered?${params.toString()}`,
    signal
  )
  return tagInventoryFilteredFromJson(response)
}

export async function fetchTagInventorySearch(
  args: TagInventorySearchArgs,
  signal?: AbortSignal
): Promise<TagInventorySearchResponse> {
  const params = new URLSearchParams({ q: args.query.trim() })
  if (args.pageToken) params.set('page_token', args.pageToken)
  params.set('page_size', String(args.pageSize ?? TAG_INVENTORY_PAGE_SIZE))
  const response = await requestInventoryJson(
    `/ontology/tags/inventory:search?${params.toString()}`,
    signal
  )
  return tagInventorySearchFromJson(response)
}

export async function fetchTagInventoryMismatches(
  args: TagInventoryMismatchesArgs = {},
  signal?: AbortSignal
): Promise<TagInventoryMismatchesResponse> {
  const params = new URLSearchParams({ status: args.status ?? 'all' })
  if (args.pageToken) params.set('page_token', args.pageToken)
  params.set('page_size', String(args.pageSize ?? TAG_INVENTORY_PAGE_SIZE))
  const response = await requestInventoryJson(
    `/ontology/tags/inventory:mismatches?${params.toString()}`,
    signal
  )
  return tagInventoryMismatchesFromJson(response)
}

export async function fetchTagInventoryHealth(
  ids: readonly string[],
  signal?: AbortSignal
): Promise<TagInventoryHealthResponse> {
  const uniqueIds = [...new Set(ids.filter((id) => id.length > 0))]
  if (uniqueIds.length === 0) return { windowSeconds: 0, results: [] }
  const response = await requestInventoryJson('/ontology/tags/inventory:health', signal, {
    ids: uniqueIds
  })
  return tagInventoryHealthFromJson(response)
}

function queryErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function useInventoryQuery<T>(
  key: string,
  fetcher: (signal: AbortSignal) => Promise<T>,
  skip = false
): TagInventoryQueryResult<T> {
  const initialCacheEntry = inventoryQueryCache.get(key)
  const [data, setData] = useState<T | undefined>(() => initialCacheEntry?.data as T | undefined)
  const [dataKey, setDataKey] = useState<string | undefined>(() =>
    initialCacheEntry ? key : undefined
  )
  const [isLoading, setIsLoading] = useState(
    () => !skip && (!initialCacheEntry || initialCacheEntry.expiresAt <= Date.now())
  )
  const [error, setError] = useState<string | undefined>(undefined)
  const [refreshTick, setRefreshTick] = useState(0)
  const requestId = useRef(0)
  const fetcherRef = useRef(fetcher)
  const forceRefetchRef = useRef(false)
  const keyRef = useRef(key)
  fetcherRef.current = fetcher
  keyRef.current = key

  useEffect(() => {
    const forceRefetch = forceRefetchRef.current
    forceRefetchRef.current = false
    if (skip) {
      requestId.current++
      setIsLoading(false)
      setError(undefined)
      return
    }

    const cached = inventoryQueryCache.get(key)
    if (!forceRefetch && cached && cached.expiresAt > Date.now()) {
      requestId.current++
      setData(cached.data as T)
      setDataKey(key)
      setIsLoading(false)
      setError(undefined)
      return
    }

    const currentRequestId = ++requestId.current
    if (cached) setData(cached.data as T)
    setIsLoading(true)
    setError(undefined)
    inventoryQueryFlight(key, fetcherRef.current)
      .then((result) => {
        if (currentRequestId !== requestId.current) return
        setData(result)
        setDataKey(key)
        setIsLoading(false)
      })
      .catch((caught: unknown) => {
        if (currentRequestId !== requestId.current) return
        setError(queryErrorMessage(caught))
        setIsLoading(false)
      })
    return () => {
      if (currentRequestId === requestId.current) requestId.current++
    }
  }, [key, refreshTick, skip])

  const refetch = useCallback(() => {
    requestId.current++
    forceRefetchRef.current = true
    cancelInventoryQueryFlight(keyRef.current)
    inventoryQueryCache.delete(keyRef.current)
    setRefreshTick((value) => value + 1)
  }, [])
  return {
    data,
    isLoading,
    isPreviousData: data !== undefined && dataKey !== key,
    error,
    refetch
  }
}

export function useTagInventoryTreeQuery(
  args: TagInventoryTreeArgs = {},
  options: { skip?: boolean } = {}
): TagInventoryQueryResult<TagInventoryTreeResponse> {
  const key = `tree:${inventoryQueryScopeKey()}:${JSON.stringify(args)}`
  return useInventoryQuery(
    key,
    (signal) => fetchTagInventoryTree(args, signal),
    options.skip === true
  )
}

export function useTagInventoryFilteredQuery(
  args: TagInventoryFilteredArgs,
  options: { skip?: boolean } = {}
): TagInventoryQueryResult<TagInventoryFilteredResponse> {
  const key = `filtered:${inventoryQueryScopeKey()}:${JSON.stringify(args)}`
  return useInventoryQuery(
    key,
    (signal) => fetchTagInventoryFiltered(args, signal),
    options.skip === true
  )
}

export function useTagInventorySearchQuery(
  args: TagInventorySearchArgs,
  options: { skip?: boolean } = {}
): TagInventoryQueryResult<TagInventorySearchResponse> {
  const key = `search:${inventoryQueryScopeKey()}:${JSON.stringify(args)}`
  return useInventoryQuery(
    key,
    (signal) => fetchTagInventorySearch(args, signal),
    options.skip === true || args.query.trim() === ''
  )
}

export function useTagInventoryMismatchesQuery(
  args: TagInventoryMismatchesArgs = {},
  options: { skip?: boolean } = {}
): TagInventoryQueryResult<TagInventoryMismatchesResponse> {
  const key = `mismatches:${inventoryQueryScopeKey()}:${JSON.stringify(args)}`
  return useInventoryQuery(
    key,
    (signal) => fetchTagInventoryMismatches(args, signal),
    options.skip === true
  )
}

export function useTagInventoryHealthQuery(
  ids: readonly string[],
  scope: { branch?: string; site?: string } = {}
): TagInventoryQueryResult<TagInventoryHealthResponse> {
  const uniqueIds = [...new Set(ids.filter((id) => id.length > 0))]
  const key = `health:${inventoryQueryScopeKey()}:${JSON.stringify({
    ids: [...uniqueIds].sort(),
    branch: scope.branch ?? '',
    site: scope.site ?? ''
  })}`
  return useInventoryQuery(
    key,
    (signal) => fetchTagInventoryHealth(uniqueIds, signal),
    uniqueIds.length === 0
  )
}
